import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FlavorPicker } from "@/components/events/FlavorPicker";

const F = ({ label, children }) => (
  <div>
    <Label className="mono text-[11px] tracking-widest text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const inputCls = "rounded-sm bg-[#0A0A0A]";

export const BookingForm = ({ catering, services = [], menu, prefill }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "", event_date: "",
    start_time: "", location: "", drink_count: "", package: "",
    service_option: "", alani_count: "", flavors: [], notes: "",
  });
  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [prefill]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/bookings", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div
        data-testid="booking-success"
        className="border border-primary bg-[#191603] p-8 text-center md:p-14"
      >
        <p className="display neon text-3xl md:text-4xl">{catering.success}</p>
        <p className="mono mt-4 text-[11px] tracking-widest text-muted-foreground">
          A CONFIRMATION IS ON ITS WAY TO YOUR INBOX.
        </p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <F label="NAME">
          <Input data-testid="booking-name" required value={form.name} onChange={set("name")}
            className={inputCls} placeholder="Jordan Ahquin" />
        </F>
        <F label="EMAIL">
          <Input data-testid="booking-email" required type="email" value={form.email}
            onChange={set("email")} className={inputCls} placeholder="you@email.com" />
        </F>
        <F label="PHONE">
          <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")}
            className={inputCls} placeholder="(808) 555-0134" />
        </F>
        <F label="EVENT TYPE">
          <select
            data-testid="booking-event-type"
            value={form.event_type}
            onChange={set("event_type")}
            className="mono h-10 w-full rounded-sm border border-border bg-[#0A0A0A] px-3 text-xs text-white"
          >
            <option value="">Select one</option>
            {(catering.event_types || []).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </F>
        <F label="EVENT DATE">
          <Input data-testid="booking-date" type="date" required value={form.event_date}
            onChange={set("event_date")} className={inputCls} />
        </F>
        <F label="START TIME">
          <Input data-testid="booking-time" type="time" value={form.start_time}
            onChange={set("start_time")} className={inputCls} />
        </F>
        <F label="LOCATION / CITY">
          <Input data-testid="booking-location" value={form.location} onChange={set("location")}
            className={inputCls} placeholder="Kapolei, HI" />
        </F>
        <F label="HOW MANY DRINKS?">
          <Input data-testid="booking-drinks" value={form.drink_count} onChange={set("drink_count")}
            className={inputCls} placeholder="150" />
        </F>
        <F label="SERVICE OPTION">
          <select
            data-testid="booking-service"
            value={form.service_option}
            onChange={set("service_option")}
            className="mono h-10 w-full rounded-sm border border-border bg-[#0A0A0A] px-3 text-xs text-white"
          >
            <option value="">Not sure yet — help me pick</option>
            {services.map((s) => (
              <option key={s.id} value={`${s.name} (${s.price_label})`}>
                {s.name} — {s.price_label}
              </option>
            ))}
          </select>
        </F>
        <F label="DIRTY ALANI UPGRADES (+$1 EACH)">
          <Input data-testid="booking-alani" value={form.alani_count} onChange={set("alani_count")}
            className={inputCls} placeholder="0" />
        </F>
      </div>

      <FlavorPicker
        menu={menu}
        selected={form.flavors}
        onChange={(flavors) => setForm((f) => ({ ...f, flavors }))}
      />

      <F label="ANYTHING ELSE WE SHOULD KNOW?">
        <Textarea data-testid="booking-notes" rows={4} value={form.notes} onChange={set("notes")}
          className={inputCls} placeholder="Indoor or outdoor, power access, flavors you want, budget…" />
      </F>

      {error ? (
        <p data-testid="booking-error" className="text-sm text-destructive">{error}</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-5">
        <Button
          type="submit"
          disabled={busy}
          data-testid="booking-submit"
          className="display rounded-sm bg-primary px-8 py-6 text-xl uppercase text-primary-foreground hover:bg-[#E6C800]"
        >
          {busy ? "Sending…" : "Request this date"}
        </Button>
        <p className="mono text-[10px] leading-relaxed tracking-widest text-muted-foreground">
          {catering.response_note}
        </p>
      </div>
    </form>
  );
};
